// What actually conflicted in a run — read off the run, not off a table.
//
// This is the shared primitive of docs/design/txn-granular-exploration.md: one
// notion of conflict, at the granularity the language actually enforces
// atomicity, that both the race detector (change (a)) and the transaction-
// granular explorer (change (b)) are built on.
//
// **Units.** A unit is one indivisible thing the scheduler could have placed
// somewhere else. A unit is only as atomic as the language guarantees:
//
//   - a *committed* `txn` → one unit, footprint = the union of the addresses it
//     read and wrote on the attempt that committed;
//   - a bare (`auto`) access → its *own* singleton unit. No grouping: bare
//     accesses really are independently interleavable, which is why
//     `lost_update` — which uses no `txn` — still has a window to find. Grouping
//     a thread's bare accesses would make its read-modify-write look atomic and
//     hide the very race the example exists for;
//   - a `txn` that did *not* commit (aborted, parked on `retry`, or still open
//     when the run ended) was rolled back, so it took effect nowhere and
//     contributes no unit at all. Retries get a fresh txn id, so keeping only
//     committed ids drops the losers automatically.
//
// Footprints are **dynamic** — the addresses this attempt actually touched, read
// from the trace — not the static `LockPlan` footprint, because a retry re-reads
// and what matters is what the winning attempt did.
//
// **Conflict.** Two units conflict when their footprints overlap on an address
// with at least one writer (WR / WW / RW) — conflict serializability, the same
// test `Transaction.validate()` makes. That covers the *potential* dependency.
// The *realized* one is finer, and the interpreter already knows it exactly: a
// read's version is its reads-from edge, recorded in `causality.deps` by the
// same code that did the load. `readFrom` reads that edge back at unit
// granularity; `concurrent` asks the same graph whether two units were ordered
// at all. A race is a *non-edge*, so both halves are needed: the realized edges
// say what the run tied together, the footprint test says what an unordered pair
// could still have collided on.

import type { Causality } from "../src/backend/interp/causality.js";
import type { Trace, TraceEvent } from "../src/backend/interp/trace.js";

export type AccessKind = "read" | "write";

/** One effective read or write, anchored to the trace row it came from. */
export interface Access {
  /** Index into `trace.events`. Stable within the run it was read from. */
  row: number;
  kind: AccessKind;
  thread: number;
  addr: string;
  line: number;
  /** The transaction it ran in, or `"auto"` when it ran outside any. */
  txn: number | "auto";
  /** For a read, the value observed; for a write, the value stored. */
  value: string;
}

/** One indivisible scheduling unit: a committed transaction, or a single bare
 *  access. Its footprint is what decides whether it conflicts with another. */
export interface Unit {
  /** `txn:<id>` for a committed transaction, `auto:<row>` for a bare access. */
  id: string;
  kind: "txn" | "auto";
  /** The committed transaction's id, or null for a bare access. */
  txn: number | null;
  thread: number;
  /** The footprint: addresses read / written by this unit. */
  reads: Set<string>;
  writes: Set<string>;
  /** The unit's accesses, in trace order. */
  accesses: Access[];
  /** Every trace row the unit occupies, ascending: its accesses, plus a
   *  transaction's `txn-begin` / `txn-commit`. Other threads' rows interleave
   *  between them — a unit is a set of rows, not a span. */
  rows: number[];
  /** The unit's serialization point: the row it appears to happen at. A
   *  transaction's commit (nothing it did is visible before that), a bare
   *  access's own row. Units are ordered by this. */
  end: number;
}

/** The units of one run, with what was thrown away to get them. */
export interface Units {
  /** In commit order — the order the run serialized them in. */
  units: Unit[];
  /** Ids of transactions that touched memory but never committed. Rolled back,
   *  so they contribute no unit. */
  dropped: number[];
  /** Every trace row that belongs to a unit → that unit. Includes a
   *  transaction's `txn-begin` / `txn-commit` rows, so a causal edge landing on
   *  a commit (which is where a transaction publishes) resolves to its unit. */
  unitOfRow: Map<number, Unit>;
}

export type ConflictKind = "WR" | "RW" | "WW";

/** Why two units conflict: the kinds of overlap, and the addresses they are on. */
export interface Conflict {
  kinds: ConflictKind[];
  addrs: string[];
}

/** The transactions that committed — the only non-`auto` accesses that took
 *  effect. Everything else was rolled back. */
export function committedTxns(events: readonly TraceEvent[]): Set<number> {
  const out = new Set<number>();
  for (const ev of events) if (ev.type === "txn-commit") out.add(ev.txn);
  return out;
}

/** Every read and write that actually happened, in trace order. */
export function effectiveAccesses(trace: Trace): Access[] {
  const committed = committedTxns(trace.events);
  const out: Access[] = [];
  trace.events.forEach((ev, row) => {
    if (ev.type !== "read" && ev.type !== "write") return;
    if (ev.txn !== "auto" && !committed.has(ev.txn)) return; // rolled back — never happened
    out.push({
      row,
      kind: ev.type,
      thread: ev.thread,
      addr: String(ev.addr),
      line: ev.line,
      txn: ev.txn,
      value: ev.value,
    });
  });
  return out;
}

/**
 * Collapse a run's accesses into units, per the unit rule above.
 *
 * Pure in the trace: the footprints are the addresses the committed attempts
 * actually touched, and the aborted attempts are simply absent.
 */
export function txnUnits(trace: Trace): Units {
  const events = trace.events;
  const committed = committedTxns(events);

  const units: Unit[] = [];
  const byTxn = new Map<number, Unit>();
  const dropped = new Set<number>();
  const unitOfRow = new Map<number, Unit>();

  const claim = (u: Unit, row: number): void => {
    u.rows.push(row);
    unitOfRow.set(row, u);
  };

  events.forEach((ev, row) => {
    if (ev.type !== "read" && ev.type !== "write") return;
    const addr = String(ev.addr);
    const access: Access = {
      row,
      kind: ev.type,
      thread: ev.thread,
      addr,
      line: ev.line,
      txn: ev.txn,
      value: ev.value,
    };
    if (ev.txn === "auto") {
      const u: Unit = {
        id: `auto:${row}`,
        kind: "auto",
        txn: null,
        thread: ev.thread,
        reads: new Set(),
        writes: new Set(),
        accesses: [access],
        rows: [],
        end: row,
      };
      (ev.type === "read" ? u.reads : u.writes).add(addr);
      units.push(u);
      claim(u, row);
      return;
    }
    if (!committed.has(ev.txn)) {
      dropped.add(ev.txn); // aborted / parked / open — rolled back
      return;
    }
    let u = byTxn.get(ev.txn);
    if (!u) {
      u = {
        id: `txn:${ev.txn}`,
        kind: "txn",
        txn: ev.txn,
        thread: ev.thread,
        reads: new Set(),
        writes: new Set(),
        accesses: [],
        rows: [],
        end: row,
      };
      byTxn.set(ev.txn, u);
      units.push(u);
    }
    (ev.type === "read" ? u.reads : u.writes).add(addr);
    u.accesses.push(access);
    claim(u, row);
  });

  // A transaction's own rows, in a second pass: `txn-begin` precedes the unit's
  // first access, so the unit did not exist yet when it went by. The commit is
  // the row that matters — it is where the transaction publishes, so it is both
  // the unit's serialization point and the row a later read's reads-from edge
  // lands on.
  events.forEach((ev, row) => {
    if (ev.type !== "txn-begin" && ev.type !== "txn-commit") return;
    const u = byTxn.get(ev.txn);
    if (!u) return;
    claim(u, row);
    if (ev.type === "txn-commit") u.end = row;
  });

  for (const u of units) u.rows.sort((a, b) => a - b);
  units.sort((a, b) => a.end - b.end || a.rows[0]! - b.rows[0]!);
  return { units, dropped: [...dropped], unitOfRow };
}

/** Whether a unit touched an address at all. */
export function touches(u: Unit, addr: string): boolean {
  return u.reads.has(addr) || u.writes.has(addr);
}

/**
 * The addresses exactly one thread ever touched in this run — **post-hoc
 * privacy**, the weaker of the two forms of the thread-local rule (see
 * docs/design/thread-local-state.md).
 *
 * A property of *the trace*, not of the program: an address private under this
 * interleaving might be shared under another. That is the right notion for a
 * view that is already one run's order — the happens-before graph — and the
 * wrong one for the enumerator, which needs *escape* (prospective, monotone, and
 * recorded by the interpreter at the point a value crosses a `go`).
 *
 * Rolled-back accesses count: an aborted attempt still names the address, and
 * counting it can only call an address shared, never private, which is the safe
 * direction.
 */
export function privateAddresses(events: readonly TraceEvent[]): Set<string> {
  // addr → the one thread that has touched it, or null once a second one has.
  const owner = new Map<string, number | null>();
  for (const ev of events) {
    if (ev.type !== "read" && ev.type !== "write") continue;
    const addr = String(ev.addr);
    const seen = owner.get(addr);
    if (seen === undefined) owner.set(addr, ev.thread);
    else if (seen !== null && seen !== ev.thread) owner.set(addr, null);
  }
  const priv = new Set<string>();
  for (const [addr, thread] of owner) if (thread !== null) priv.add(addr);
  return priv;
}

/**
 * The footprint conflict between two units, or null when they are independent.
 *
 * This is the store's own test — overlap on an address with at least one writer
 * — and nothing else. Same-thread units are left to the caller: they are
 * program-ordered, so their conflict is not a scheduling choice.
 */
export function conflict(a: Unit, b: Unit): Conflict | null {
  const kinds: ConflictKind[] = [];
  const addrs = new Set<string>();
  const overlap = (kind: ConflictKind, from: Set<string>, to: Set<string>): void => {
    for (const addr of from) {
      if (!to.has(addr)) continue;
      if (!kinds.includes(kind)) kinds.push(kind);
      addrs.add(addr);
    }
  };
  overlap("WR", a.writes, b.reads);
  overlap("RW", a.reads, b.writes);
  overlap("WW", a.writes, b.writes);
  if (kinds.length === 0) return null;
  return { kinds, addrs: [...addrs].sort() };
}

/**
 * Ancestor sets over the causal graph: `anc[i]` is every event `i` transitively
 * depends on — happens-before, as reachability. `deps[i]` only ever names
 * earlier events, so one forward pass suffices.
 */
export function ancestors(deps: readonly number[][]): Set<number>[] {
  const anc: Set<number>[] = [];
  for (let i = 0; i < deps.length; i++) {
    const s = new Set<number>();
    for (const d of deps[i] ?? []) {
      s.add(d);
      for (const a of anc[d] ?? []) s.add(a);
    }
    anc[i] = s;
  }
  return anc;
}

/** Whether some row of `a` is a causal ancestor of some row of `b` — i.e. `a`
 *  happens-before `b`, at least in part. */
export function reaches(a: Unit, b: Unit, anc: readonly Set<number>[]): boolean {
  for (const rb of b.rows) {
    const before = anc[rb];
    if (!before) continue;
    for (const ra of a.rows) if (before.has(ra)) return true;
  }
  return false;
}

/** Whether neither unit happens-before the other — a genuine either-order
 *  decision, and the half of dependency that no realized edge records. */
export function concurrent(a: Unit, b: Unit, anc: readonly Set<number>[]): boolean {
  return !reaches(a, b, anc) && !reaches(b, a, anc);
}

/**
 * The row in `reader` that read `addr` from `writer`, or null if none did.
 *
 * This is the realized reads-from edge, read back at unit granularity. The
 * interpreter recorded it when it did the load — a read's version *is* its
 * reads-from edge — so this only has to resolve the causal predecessor to the
 * unit that published: a bare write publishes at its own row, a transaction at
 * its commit row.
 */
export function readFrom(
  reader: Unit,
  writer: Unit,
  addr: string,
  causality: Causality,
  unitOfRow: ReadonlyMap<number, Unit>
): number | null {
  if (!writer.writes.has(addr)) return null;
  for (const access of reader.accesses) {
    if (access.kind !== "read" || access.addr !== addr) continue;
    for (const dep of causality.deps[access.row] ?? []) {
      if (unitOfRow.get(dep) === writer) return access.row;
    }
  }
  return null;
}
